import React, { useState } from 'react';
import { Flower2, Mail, ArrowLeft, CheckCircle, Phone, ShieldCheck, Key, Lock, Eye, EyeOff, UserCheck, Copy } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Link } from 'react-router-dom';
import { toast } from '../hooks/use-toast';
import api from '../services/api';

const ForgotPassword = () => {
  const [method, setMethod] = useState(null);
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  // Recovery code reset states
  const [showCodeReset, setShowCodeReset] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [newRecoveryCode, setNewRecoveryCode] = useState(null);

  // Identity verification states (Option B)
  const [showIdentityVerify, setShowIdentityVerify] = useState(false);
  const [verifyFirstName, setVerifyFirstName] = useState('');
  const [verifyLastName, setVerifyLastName] = useState('');
  const [tempCode, setTempCode] = useState(null);
  const [showTempCodeReset, setShowTempCodeReset] = useState(false);
  const [tempCodeInput, setTempCodeInput] = useState('');

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: identifier });
      setEmailSent(true);
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail || 'Une erreur est survenue', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/auth/forgot-password', { phone: identifier });
      const data = response.data;
      
      if (data.method === 'email' && data.hasEmail) {
        toast({ title: 'Email envoye', description: `Un lien a ete envoye a ${data.maskedEmail}` });
        setEmailSent(true);
      } else if (data.method === 'code' && data.hasRecoveryCode) {
        setShowCodeReset(true);
      } else if (data.method === 'identity_verify') {
        setShowIdentityVerify(true);
      }
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail || 'Une erreur est survenue', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleCodeReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast({ title: 'Erreur', description: 'Les mots de passe ne correspondent pas', variant: 'destructive' });
      return;
    }
    if (newPassword.length < 8) {
      toast({ title: 'Erreur', description: 'Le mot de passe doit contenir au moins 8 caracteres', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const response = await api.post('/auth/reset-by-recovery-code', {
        phone: identifier,
        recoveryCode: recoveryCode.trim().toUpperCase(),
        newPassword
      });
      setResetSuccess(true);
      setNewRecoveryCode(response.data.newRecoveryCode);
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail || 'Code invalide', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleIdentityVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/auth/verify-identity-reset', {
        phone: identifier,
        firstName: verifyFirstName.trim(),
        lastName: verifyLastName.trim()
      });
      setTempCode(response.data.tempCode);
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail || 'Verification echouee', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleTempCodeReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast({ title: 'Erreur', description: 'Les mots de passe ne correspondent pas', variant: 'destructive' });
      return;
    }
    if (newPassword.length < 8) {
      toast({ title: 'Erreur', description: 'Minimum 8 caracteres', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const response = await api.post('/auth/reset-by-temp-code', {
        phone: identifier,
        tempCode: tempCodeInput.trim().toUpperCase(),
        newPassword
      });
      setResetSuccess(true);
      setNewRecoveryCode(response.data.newRecoveryCode);
    } catch (error) {
      toast({ title: 'Erreur', description: error.response?.data?.detail || 'Code invalide', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast({ title: 'Copie !', description: 'Code copie dans le presse-papier' });
    });
  };

  // ===== RENDER: Success after reset =====
  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 bg-white/10 backdrop-blur-md border-white/20 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Mot de passe modifie !</h2>
          <p className="text-pink-200 mb-6">Votre mot de passe a ete reinitialise avec succes.</p>
          
          {newRecoveryCode && (
            <div className="mb-6 p-4 bg-orange-500/20 border border-orange-400/30 rounded-xl">
              <p className="text-orange-200 text-sm mb-2">Votre nouveau code de recuperation :</p>
              <div className="flex items-center justify-center gap-2">
                <p className="text-2xl font-mono font-bold text-white tracking-wider" data-testid="new-recovery-code">
                  {newRecoveryCode}
                </p>
                <button onClick={() => copyToClipboard(newRecoveryCode)} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                  <Copy className="w-5 h-5 text-orange-300" />
                </button>
              </div>
              <p className="text-orange-200 text-xs mt-2">Notez-le en lieu sur ! Il vous servira en cas d'oubli de mot de passe.</p>
            </div>
          )}
          
          <Link to="/login">
            <Button className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700" data-testid="go-login-btn">
              Se connecter
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  // ===== RENDER: Email sent confirmation =====
  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 bg-white/10 backdrop-blur-md border-white/20 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Email envoye !</h2>
          <p className="text-pink-200 mb-6">
            Si un compte existe avec cette adresse, vous recevrez un lien de reinitialisation. Verifiez egalement votre dossier spam.
          </p>
          <Link to="/login">
            <Button variant="outline" className="w-full border-white/30 text-white hover:bg-white/10">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour a la connexion
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  // ===== RENDER: Temp code displayed + reset form (Option B) =====
  if (tempCode && !showTempCodeReset) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 bg-white/10 backdrop-blur-md border-white/20 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-blue-500/20 flex items-center justify-center">
            <Key className="w-8 h-8 text-blue-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Code de reinitialisation</h2>
          <p className="text-pink-200 text-sm mb-6">Voici votre code temporaire. Notez-le, il expire dans 15 minutes.</p>
          
          <div className="p-6 bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-400/30 rounded-xl mb-6">
            <div className="flex items-center justify-center gap-3">
              <p className="text-4xl font-mono font-bold text-white tracking-[0.3em]" data-testid="temp-reset-code">
                {tempCode}
              </p>
              <button onClick={() => copyToClipboard(tempCode)} className="p-2 hover:bg-white/10 rounded-lg transition-colors" data-testid="copy-temp-code">
                <Copy className="w-6 h-6 text-blue-300" />
              </button>
            </div>
            <p className="text-blue-200 text-xs mt-3">Valable 15 minutes</p>
          </div>

          <Button 
            onClick={() => setShowTempCodeReset(true)}
            className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 h-12"
            data-testid="proceed-reset-btn"
          >
            Reinitialiser mon mot de passe
          </Button>
        </Card>
      </div>
    );
  }

  // ===== RENDER: Temp code reset form =====
  if (showTempCodeReset) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 bg-white/10 backdrop-blur-md border-white/20">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Nouveau mot de passe</h1>
            <p className="text-pink-200 text-sm">Entrez le code temporaire et votre nouveau mot de passe</p>
          </div>

          <form onSubmit={handleTempCodeReset} className="space-y-5">
            <div>
              <Label className="text-pink-200">Code temporaire</Label>
              <div className="relative mt-1">
                <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-pink-300" />
                <Input required value={tempCodeInput} onChange={(e) => setTempCodeInput(e.target.value)}
                  placeholder="Code a 6 caracteres"
                  className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-pink-300/50 font-mono tracking-wider uppercase"
                  data-testid="temp-code-input" />
              </div>
            </div>
            <div>
              <Label className="text-pink-200">Nouveau mot de passe</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-pink-300" />
                <Input type={showPassword ? 'text' : 'password'} required minLength={8}
                  value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 8 caracteres"
                  className="pl-10 pr-10 bg-white/10 border-white/20 text-white placeholder:text-pink-300/50"
                  data-testid="new-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-pink-300">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-pink-200">Confirmer le mot de passe</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-pink-300" />
                <Input type={showPassword ? 'text' : 'password'} required minLength={8}
                  value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirmer" className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-pink-300/50"
                  data-testid="confirm-password-input" />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 h-12" data-testid="reset-password-btn">
              {loading ? 'Reinitialisation...' : 'Reinitialiser le mot de passe'}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <button onClick={() => { setShowTempCodeReset(false); setTempCode(null); setShowIdentityVerify(true); }} className="text-pink-300 hover:text-white text-sm">
              <ArrowLeft className="w-4 h-4 inline mr-1" />Retour
            </button>
          </div>
        </Card>
      </div>
    );
  }

  // ===== RENDER: Identity verification (Option B) =====
  if (showIdentityVerify) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 bg-white/10 backdrop-blur-md border-white/20">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
              <UserCheck className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Verification d'identite</h1>
            <p className="text-pink-200 text-sm">Confirmez vos informations pour obtenir un code de reinitialisation</p>
          </div>

          <form onSubmit={handleIdentityVerify} className="space-y-5">
            <div>
              <Label className="text-pink-200">Prenom (tel qu'a l'inscription)</Label>
              <Input required value={verifyFirstName} onChange={(e) => setVerifyFirstName(e.target.value)}
                placeholder="Votre prenom" className="bg-white/10 border-white/20 text-white placeholder:text-pink-300/50"
                data-testid="verify-firstname" />
            </div>
            <div>
              <Label className="text-pink-200">Nom (tel qu'a l'inscription)</Label>
              <Input required value={verifyLastName} onChange={(e) => setVerifyLastName(e.target.value)}
                placeholder="Votre nom" className="bg-white/10 border-white/20 text-white placeholder:text-pink-300/50"
                data-testid="verify-lastname" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 h-12" data-testid="verify-identity-btn">
              {loading ? 'Verification...' : 'Verifier mon identite'}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <button onClick={() => { setShowIdentityVerify(false); setMethod('phone'); }} className="text-pink-300 hover:text-white text-sm">
              <ArrowLeft className="w-4 h-4 inline mr-1" />Retour
            </button>
          </div>
        </Card>
      </div>
    );
  }

  // ===== RENDER: Recovery code reset form =====
  if (showCodeReset) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 bg-white/10 backdrop-blur-md border-white/20">
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
              <Key className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Reinitialisation</h1>
            <p className="text-pink-200 text-sm">Entrez votre code de recuperation et votre nouveau mot de passe</p>
          </div>

          <form onSubmit={handleCodeReset} className="space-y-5">
            <div>
              <Label className="text-pink-200">Code de recuperation</Label>
              <div className="relative mt-1">
                <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-pink-300" />
                <Input required value={recoveryCode} onChange={(e) => setRecoveryCode(e.target.value)}
                  placeholder="FDV-XXXX-XXXX"
                  className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-pink-300/50 font-mono tracking-wider uppercase"
                  data-testid="recovery-code-input" />
              </div>
            </div>
            <div>
              <Label className="text-pink-200">Nouveau mot de passe</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-pink-300" />
                <Input type={showPassword ? 'text' : 'password'} required minLength={8}
                  value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 8 caracteres"
                  className="pl-10 pr-10 bg-white/10 border-white/20 text-white placeholder:text-pink-300/50"
                  data-testid="new-password-input" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-pink-300">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-pink-200">Confirmer le mot de passe</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-pink-300" />
                <Input type={showPassword ? 'text' : 'password'} required minLength={8}
                  value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirmer" className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-pink-300/50"
                  data-testid="confirm-password-input" />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 h-12" data-testid="reset-password-btn">
              {loading ? 'Reinitialisation...' : 'Reinitialiser le mot de passe'}
            </Button>
          </form>
          <div className="mt-6 text-center space-y-2">
            <button onClick={() => { setShowCodeReset(false); setShowIdentityVerify(true); }} className="text-blue-300 hover:text-white text-sm block w-full">
              Code perdu ? Verifier mon identite
            </button>
            <button onClick={() => { setShowCodeReset(false); setMethod('phone'); }} className="text-pink-300 hover:text-white text-sm">
              <ArrowLeft className="w-4 h-4 inline mr-1" />Retour
            </button>
          </div>
        </Card>
      </div>
    );
  }

  // ===== RENDER: Method selection or form =====
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8 bg-white/10 backdrop-blur-md border-white/20">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
            <Flower2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Mot de passe oublie ?</h1>
          <p className="text-pink-200 text-sm">
            {!method ? "Comment vous etes-vous inscrit(e) ?"
              : method === 'email' ? "Entrez votre email pour recevoir un lien"
              : "Entrez votre numero de telephone"}
          </p>
        </div>

        {!method ? (
          <div className="space-y-4">
            <Button onClick={() => setMethod('email')} variant="outline"
              className="w-full h-14 border-white/20 text-white hover:bg-white/10 flex items-center gap-3 justify-start px-6"
              data-testid="forgot-method-email">
              <Mail className="w-5 h-5 text-pink-300" />
              <div className="text-left">
                <p className="font-semibold">Avec mon email</p>
                <p className="text-xs text-pink-200/70">Recevoir un lien par email</p>
              </div>
            </Button>
            <Button onClick={() => setMethod('phone')} variant="outline"
              className="w-full h-14 border-white/20 text-white hover:bg-white/10 flex items-center gap-3 justify-start px-6"
              data-testid="forgot-method-phone">
              <Phone className="w-5 h-5 text-pink-300" />
              <div className="text-left">
                <p className="font-semibold">Avec mon telephone</p>
                <p className="text-xs text-pink-200/70">Code de recuperation ou verification</p>
              </div>
            </Button>
          </div>
        ) : method === 'email' ? (
          <form onSubmit={handleEmailSubmit} className="space-y-6">
            <div>
              <Label className="text-pink-200">Adresse email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-pink-300" />
                <Input type="email" required value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="votre@email.com" className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-pink-300/50"
                  data-testid="forgot-email-input" />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 h-12">
              {loading ? 'Envoi en cours...' : 'Envoyer le lien'}
            </Button>
          </form>
        ) : (
          <form onSubmit={handlePhoneSubmit} className="space-y-6">
            <div>
              <Label className="text-pink-200">Numero de telephone</Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-pink-300" />
                <Input type="tel" required value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="+225 XX XX XX XX XX" className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-pink-300/50"
                  data-testid="forgot-phone-input" />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 h-12">
              {loading ? 'Verification...' : 'Continuer'}
            </Button>
          </form>
        )}

        <div className="mt-6 text-center">
          {method ? (
            <button onClick={() => { setMethod(null); setIdentifier(''); }} className="text-pink-300 hover:text-white text-sm transition-colors">
              <ArrowLeft className="w-4 h-4 inline mr-1" />Autre methode
            </button>
          ) : (
            <Link to="/login" className="text-pink-300 hover:text-white text-sm transition-colors">
              <ArrowLeft className="w-4 h-4 inline mr-1" />Retour a la connexion
            </Link>
          )}
        </div>
      </Card>
    </div>
  );
};

export default ForgotPassword;
